"use client";

import {
  AutoStoriesOutlined,
  BusinessOutlined,
  DescriptionOutlined,
  ChecklistOutlined,
  FavoriteBorderOutlined,
  GppGoodOutlined,
  HandshakeOutlined,
  MosqueOutlined,
  SpeedOutlined,
  TouchAppOutlined,
  VerifiedOutlined,
  VolunteerActivismOutlined,
  ShieldOutlined,
} from "@mui/icons-material";
import { Box, Card, Container, Typography } from "@mui/material";
import Image from "next/image";
import { CardGrid, Section, Steps, TextLink } from "../components/Sections";
import { HomeOrganisationsRow } from "../components/Organisations";
import { StoreBadges } from "../components/StoreBadges";
import { useDocumentTitle, useLanguage } from "../components/useLanguage";
import { APP_STORE, PLAY_STORE } from "../content/common";
import { STORE_FIGURES, TULUS_APP } from "../content/tulusApp";
import { SYARIAH_DOCS } from "../content/syariah";
import { TULUS } from "../brand";
// The site's own pictures of the app as it ships (see content/tulusApp.ts).
import HeroImage from "../../public/images/app_img4.webp";
import ScreenHome from "../../public/images/app_img2.webp";
import ScreenOrganisations from "../../public/images/app_img3.webp";

const FEATURE_ICONS = [VolunteerActivismOutlined, FavoriteBorderOutlined, MosqueOutlined, ShieldOutlined, ChecklistOutlined, AutoStoriesOutlined];
const REVIEW_ICONS = [SpeedOutlined, TouchAppOutlined, HandshakeOutlined];
const SCREENS = [ScreenHome, ScreenOrganisations];

/** The Tulus app: the contributor app for zakat, sadaqah, infaq and waqf. */
export default function TulusAppView() {
  const [lang] = useLanguage();
  const c = TULUS_APP[lang];
  useDocumentTitle(c.meta.title);
  const locale = lang === "ms" ? "ms-MY" : "en-MY";
  const asOf = new Date(STORE_FIGURES.asOf + "T00:00:00+08:00").toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Kuala_Lumpur" });
  const badges = { appStore: { href: APP_STORE, label: c.hero.appStore }, googlePlay: { href: PLAY_STORE, label: c.hero.googlePlay } };
  const stores = [
    { name: c.ratings.googlePlay, href: PLAY_STORE, rating: STORE_FIGURES.googlePlay.rating, lines: [c.ratings.reviews(STORE_FIGURES.googlePlay.reviews), c.ratings.downloads(STORE_FIGURES.googlePlay.downloads)] },
    { name: c.ratings.appStore, href: APP_STORE, rating: STORE_FIGURES.appStore.rating, lines: [c.ratings.ratingsCount(STORE_FIGURES.appStore.ratings)] },
  ];

  return (
    <>
      <Box component="section" sx={{ backgroundColor: TULUS.mist, borderBottom: `1px solid ${TULUS.line}` }}>
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 }, display: "grid", gap: { xs: 4, md: 6 }, gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 420px" }, alignItems: "center" }}>
          <Box>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: TULUS.cyanText, mb: 1.5 }}>
              {c.hero.eyebrow}
            </Typography>
            <Typography variant="h1" sx={{ fontSize: { xs: "2.1rem", md: "3rem" }, lineHeight: 1.15, color: TULUS.ink, textWrap: "balance", mb: 2.5 }}>
              {c.hero.title}
            </Typography>
            {c.hero.lead.map((p) => (
              <Typography key={p} sx={{ fontSize: { xs: "1.02rem", md: "1.12rem" }, color: TULUS.ink, lineHeight: 1.7, mb: 2, maxWidth: 640 }}>
                {p}
              </Typography>
            ))}
            <Box sx={{ mt: 3 }}>
              <StoreBadges {...badges} />
            </Box>
          </Box>
          <Box sx={{ justifySelf: "center", width: { xs: 300, md: 420 } }}>
            <Image src={HeroImage} alt={c.hero.imageAlt} priority sizes="(max-width: 900px) 300px, 420px" style={{ width: "100%", height: "auto" }} />
          </Box>
        </Container>
      </Box>

      <Section title={c.ratings.title}>
        <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, maxWidth: 760 }}>
          {stores.map((s) => (
            <Card key={s.name} component="a" href={s.href} target="_blank" rel="noopener noreferrer" sx={{ p: 3, textDecoration: "none", "&:hover": { borderColor: TULUS.cyan } }}>
              <Typography sx={{ color: TULUS.mutedText, fontWeight: 500, mb: 0.5 }}>{s.name}</Typography>
              <Typography sx={{ fontSize: "2.4rem", fontWeight: 700, color: TULUS.cyanText, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
                {s.rating} <Box component="span" aria-hidden sx={{ color: "#F5A623", fontSize: "1.8rem" }}>★</Box>
              </Typography>
              <Typography sx={{ color: TULUS.ink, mb: 1 }}>{c.ratings.stars}</Typography>
              {s.lines.map((l) => (
                <Typography key={l} sx={{ color: TULUS.mutedText, fontSize: "0.95rem" }}>{l}</Typography>
              ))}
            </Card>
          ))}
        </Box>
        <Typography sx={{ fontSize: "0.85rem", color: TULUS.mutedText, mt: 1.5 }}>{c.ratings.source(asOf)}</Typography>
      </Section>

      <Section title={c.features.title} tone="mist">
        <CardGrid items={c.features.items.map((it, i) => ({ ...it, icon: FEATURE_ICONS[i] }))} />
      </Section>

      <Section title={c.how.title}>
        <Box sx={{ display: "grid", gap: { xs: 4, md: 6 }, gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" }, alignItems: "center" }}>
          <Steps items={c.how.steps} />
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            {c.how.screens.map((sc, i) => (
              <Box key={sc.alt} component="figure" sx={{ m: 0, width: { xs: 150, sm: 200 } }}>
                <Image src={SCREENS[i]} alt={sc.alt} sizes="200px" style={{ width: "100%", height: "auto" }} />
                <Typography component="figcaption" sx={{ mt: 1, fontSize: "0.9rem", color: TULUS.mutedText, textAlign: "center" }}>{sc.caption}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Section>

      <Section title={c.reviews.title} intro={[c.reviews.intro]} tone="mist">
        <CardGrid items={c.reviews.items.map((it, i) => ({ ...it, icon: REVIEW_ICONS[i] }))} />
        <Box sx={{ mt: 3, display: "flex", flexWrap: "wrap", columnGap: 4, rowGap: 1 }}>
          <TextLink action={{ label: c.reviews.googlePlay, href: PLAY_STORE }} />
          <TextLink action={{ label: c.reviews.appStore, href: APP_STORE }} />
        </Box>
      </Section>

      <Section title={c.trust.title}>
        <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" } }}>
          <Card sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1.25 }}>
            <GppGoodOutlined aria-hidden sx={{ color: TULUS.cyan, fontSize: 32 }} />
            <Typography variant="h3" sx={{ fontSize: "1.1rem", color: TULUS.ink }}>{c.trust.syariahTitle}</Typography>
            <Typography sx={{ color: TULUS.mutedText, lineHeight: 1.6 }}>{c.trust.syariah}</Typography>
            <Box sx={{ mt: "auto", pt: 1 }}>
              <Box
                component="a"
                href={SYARIAH_DOCS.app}
                target="_blank"
                rel="noopener"
                hrefLang="ms"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, color: TULUS.cyanText, fontWeight: 500, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
              >
                <DescriptionOutlined sx={{ fontSize: 20 }} aria-hidden />
                {c.trust.syariahLink}
              </Box>
            </Box>
          </Card>
          <Card sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1.25 }}>
            <BusinessOutlined aria-hidden sx={{ color: TULUS.cyan, fontSize: 32 }} />
            <Typography variant="h3" sx={{ fontSize: "1.1rem", color: TULUS.ink }}>{c.trust.companyTitle}</Typography>
            <Typography sx={{ color: TULUS.mutedText, lineHeight: 1.6 }}>{c.trust.company}</Typography>
            <Box sx={{ mt: "auto", pt: 1 }}>
              <TextLink action={{ label: c.trust.companyLink, href: "/tentang-kami" }} />
            </Box>
          </Card>
          <Card sx={{ p: 3, display: "flex", flexDirection: "column", gap: 1.25 }}>
            <VerifiedOutlined aria-hidden sx={{ color: TULUS.cyan, fontSize: 32 }} />
            <Typography variant="h3" sx={{ fontSize: "1.1rem", color: TULUS.ink }}>{c.trust.institutionsTitle}</Typography>
            <Typography sx={{ color: TULUS.mutedText, lineHeight: 1.6 }}>{c.trust.institutions}</Typography>
          </Card>
        </Box>
      </Section>
      {/* The approved zakat institutions (content/organisations.ts), as on Home. */}
      <HomeOrganisationsRow lang={lang} />

      <Section title={c.institutions.title} intro={[c.institutions.body]} tone="mist">
        <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 4, rowGap: 1 }}>
          <TextLink action={{ label: c.institutions.action, href: "/hubungi-kami" }} />
          <TextLink action={{ label: c.institutions.solutions, href: "/penyelesaian" }} />
        </Box>
      </Section>

      <Box component="section" sx={{ py: { xs: 6, md: 8 }, textAlign: "center" }}>
        <Container maxWidth="sm">
          <Typography variant="h2" sx={{ fontSize: { xs: "1.5rem", md: "1.9rem" }, color: TULUS.ink, mb: 1.5 }}>{c.closing.title}</Typography>
          <Typography sx={{ color: TULUS.ink, mb: 3 }}>{c.closing.body}</Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <StoreBadges {...badges} />
          </Box>
        </Container>
      </Box>
    </>
  );
}
